import {SearchPageType} from '../../../search-config';
import {getDocumentPreviewApiUrl, getFilePreviewApiUrl, getHighlightApiUrl} from '../../../../services/search-service';
import {AbortCategory, fetchFile, fetchWithBody, postJsonWithBody} from '../../../../services/fetcher';
import {HibuDocumentPreviewResponse, HibuHighlightResponse} from '../../../../common/pdf-viewer/PdfViewer.types';
import {DocumentHighlightsPayload, PreviewDocument} from './docPreviewRow-types';
import {HibuHitDoc} from '../../../search-types';
import {getCurrentLanguage} from '../../../../i18n-service';
import {getErrorMessage} from '../../../../utils/utils';

export async function fetchPlainDocument(
    searchPageType: SearchPageType,
    documentId: string,
    containerName: string,
): Promise<HibuHitDoc | undefined> {
    const previewRequest = {
        container: containerName,
        displayLanguage: getCurrentLanguage(),
        documentId: documentId,
    };
    try {
        const response = await postJsonWithBody<HibuDocumentPreviewResponse | undefined>(
            getDocumentPreviewApiUrl(searchPageType),
            JSON.stringify(previewRequest),
            AbortCategory.DETAILS,
        );

        return response?.doc;
    } catch (error) {
        console.log(error);
        return undefined;
    }
}

export async function fetchPdfDocument(
    previewLink: string,
): Promise<PreviewDocument | undefined> {
    try {
        const response: Blob = await fetchFile(
            getFilePreviewApiUrl(SearchPageType.BASIC),
            JSON.stringify({link: previewLink}),
            AbortCategory.PREVIEW_FILE);
        if (response) {
            return {
                documentBlob: response,
            };
        }
        return undefined;
    } catch (error: any) {
        console.log('Error: ', getErrorMessage(error));
        return {
            error: error,
        };
    }
}

export async function fetchHighlightedDocument(
    docId: string,
    query: string,
    containerName: string,
    previewPath?: string,
): Promise<DocumentHighlightsPayload | undefined> {
    const location = getHighlightApiUrl(SearchPageType.BASIC);
    const language = getCurrentLanguage();
    const hlRequest = {
        container: containerName,
        documentId: docId,
        relativeFilePath: previewPath,
        query: query,
        language: language,
        fields: ['content', 'title'],
    };
    const data = await fetchWithBody<HibuHighlightResponse | undefined>(
        location,
        'POST',
        JSON.stringify(hlRequest),
        AbortCategory.PREVIEW_HIGHLIGHT,
    );
    if (!data) {
        return undefined; // Fetch was aborted. Errors reject instead, so the caller can tell them apart.
    }
    if (typeof data === 'object' && !('querySpans' in data)) {
        console.warn('highlightDocument returned no querySpans; the backend predates query spans');
    }
    return {
        title: data.textWithHighlights?.title,
        content: data.textWithHighlights?.content,
        querySpans: data.querySpans ?? [],
    };
}
